import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { toast } from "react-toastify";
import {
  listLabCalculations,
  createLabCalculation,
  updateLabCalculation,
  deleteLabCalculation,
  getLabCalculationPresets,
  evaluateLabCalculation,
} from "api/labMasters";
import { getTests } from "api/reportTemplate";
import {
  canEditRecord,
  isGlobalRecord,
  readOnlyReason,
  truncateFormula,
  findDuplicateInputKeyIndex,
  normaliseParameterName,
  buildPreviewFields,
  describePreview,
  STATUS_FILTERS,
} from "utils/labMasterUi";

const PER_PAGE_OPTIONS = [10, 20, 50];
const IS_PLATFORM_ADMIN = true;

const emptyInput = () => ({ key: "", test: "", parameterName: "" });

const toFormInputs = (inputs) =>
  (Array.isArray(inputs) ? inputs : []).map((input) => ({
    key: input?.key ?? "",
    test:
      input?.test && typeof input.test === "object" ?
        String(input.test._id)
      : String(input?.test ?? ""),
    parameterName: input?.parameterName ?? "",
  }));

const ScopeChip = ({ record }) => (
  <Chip
    size="small"
    label={isGlobalRecord(record) ? "Global" : "Facility"}
    title={
      isGlobalRecord(record) ?
        "Global — Managed by DRCFO Admin"
      : "Facility — Managed by your Hospital/Clinic"
    }
    sx={{
      height: 22,
      fontSize: 11,
      fontWeight: 700,
      bgcolor: isGlobalRecord(record) ? "#EFF6FF" : "#EEF2FF",
      color: isGlobalRecord(record) ? "#1D4ED8" : "#4338CA",
    }}
  />
);

const StatusChip = ({ active }) => (
  <Chip
    size="small"
    label={active ? "Active" : "Inactive"}
    sx={{
      height: 22,
      fontSize: 11,
      fontWeight: 700,
      bgcolor: active ? "#ECFDF5" : "#F3F4F6",
      color: active ? "#047857" : "#6B7280",
    }}
  />
);

const CalculationFormDialog = ({ open, mode, calculation, tests, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const readOnly = mode === "view";
  const isEdit = mode === "edit";

  const [name, setName] = useState("");
  const [outputTest, setOutputTest] = useState("");
  const [outputParameterName, setOutputParameterName] = useState("");
  const [formula, setFormula] = useState("");
  const [decimalPlaces, setDecimalPlaces] = useState(2);
  const [isActive, setIsActive] = useState(true);
  const [inputs, setInputs] = useState([emptyInput()]);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState("");
  const [presets, setPresets] = useState([]);

  useEffect(() => {
    if (!open) return;
    setSubmitted(false);
    setServerError("");
    setName(calculation?.name || "");
    setOutputTest(
      calculation?.outputTest && typeof calculation.outputTest === "object" ?
        String(calculation.outputTest._id)
      : String(calculation?.outputTest ?? ""),
    );
    setOutputParameterName(calculation?.outputParameterName || "");
    setFormula(calculation?.formula || "");
    setDecimalPlaces(
      calculation?.decimalPlaces === undefined ? 2 : calculation.decimalPlaces,
    );
    setIsActive(calculation?.isActive !== false);
    const loaded = toFormInputs(calculation?.inputs);
    setInputs(loaded.length ? loaded : [emptyInput()]);
  }, [open, calculation]);

  useEffect(() => {
    if (!open || readOnly || isEdit) return;
    (async () => setPresets(await getLabCalculationPresets()))();
  }, [open, readOnly, isEdit]);

  const duplicateKeyIndex = useMemo(
    () => findDuplicateInputKeyIndex(inputs),
    [inputs],
  );

  const updateInput = (index, field, value) =>
    setInputs((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );

  const applyPreset = (preset) => {
    setName(preset.name || "");
    setOutputParameterName(preset.outputParameterName || "");
    setFormula(preset.formula || "");
    setDecimalPlaces(
      preset.decimalPlaces === undefined ? 2 : preset.decimalPlaces,
    );
    setInputs(
      (preset.inputs || []).map((input) => ({
        key: input.key || "",
        test: "",
        parameterName: input.parameterName || "",
      })),
    );
  };

  const handleSubmit = async () => {
    setSubmitted(true);
    setServerError("");
    if (!name.trim() || !outputParameterName.trim() || !formula.trim()) return;
    if (duplicateKeyIndex !== -1) return;

    const payload = {
      name: name.trim(),
      outputTest: outputTest || null,
      outputParameterName: normaliseParameterName(outputParameterName),
      formula: formula.trim(),
      inputs: inputs
        .filter((input) => input.key.trim() || input.parameterName.trim())
        .map((input) => ({
          key: input.key.trim(),
          ...(input.test ? { test: input.test } : {}),
          parameterName: normaliseParameterName(input.parameterName),
        })),
      decimalPlaces: Number(decimalPlaces),
      isActive,
    };

    setSaving(true);
    const res =
      isEdit ?
        await updateLabCalculation(calculation?._id, payload)
      : await createLabCalculation(payload);
    setSaving(false);

    if (res.ok) {
      onClose(true, isEdit ? "Calculation updated" : "Calculation created");
      return;
    }
    setServerError(res.error || "Something went wrong. Please try again.");
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 3 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 17 }}>
          {readOnly ?
            "Calculation details"
          : isEdit ?
            "Edit calculation"
          : "Add calculation"}
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: "#6B7280" }}>
          Derived parameters (MCV, LDL, Anion Gap…) evaluated by the server.
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: "#F9FAFB" }}>
        {serverError && (
          <Box
            role="alert"
            sx={{
              mb: 2,
              p: 1.25,
              borderRadius: 2,
              border: "1px solid #FECACA",
              bgcolor: "#FEF2F2",
              color: "#B91C1C",
              fontSize: 12.5,
              fontWeight: 600,
            }}
          >
            {serverError}
          </Box>
        )}

        {!readOnly && !isEdit && presets.length > 0 && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 2,
              border: "1px solid #C7D2FE",
              bgcolor: "#EEF2FF",
            }}
          >
            <Typography
              sx={{ fontSize: 12.5, fontWeight: 700, color: "#3730A3", mb: 1 }}
            >
              <AutoFixHighIcon sx={{ fontSize: 14, mr: 0.5 }} />
              Start from a standard calculation
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {presets.map((preset) => (
                <Chip
                  key={preset.name}
                  size="small"
                  label={preset.name}
                  onClick={() => applyPreset(preset)}
                  sx={{ fontSize: 11, fontWeight: 600, bgcolor: "#fff" }}
                />
              ))}
            </Box>
            <Typography sx={{ mt: 1, fontSize: 11, color: "#3730A3" }}>
              A preset only fills this form — nothing is saved until you create
              the calculation.
            </Typography>
          </Box>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Calculation Name"
              required
              disabled={readOnly}
              value={name}
              error={submitted && !name.trim()}
              helperText={
                submitted && !name.trim() ? "Calculation name is required" : ""
              }
              onChange={(e) => setName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small" disabled={readOnly}>
              <InputLabel id="calc-output-test">Output Test</InputLabel>
              <Select
                labelId="calc-output-test"
                label="Output Test"
                value={outputTest}
                onChange={(e) => setOutputTest(e.target.value)}
              >
                <MenuItem value="">Not linked to a test</MenuItem>
                {tests.map((test) => (
                  <MenuItem key={test._id} value={test._id}>
                    {test.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Output Parameter"
              required
              disabled={readOnly}
              value={outputParameterName}
              error={submitted && !outputParameterName.trim()}
              helperText={
                submitted && !outputParameterName.trim() ?
                  "Output parameter is required"
                : "The report template parameter this calculation fills."
              }
              onChange={(e) => setOutputParameterName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Decimal Places"
              disabled={readOnly}
              value={decimalPlaces}
              inputProps={{ min: 0, max: 6 }}
              helperText="0–6. Applied to the calculated result."
              onChange={(e) => setDecimalPlaces(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isActive}
                  disabled={readOnly}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                Input parameters *
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: "#6B7280" }}>
                Give each input a short key and point it at the parameter it
                reads from. Use those keys in the formula.
              </Typography>
            </Box>
            {!readOnly && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setInputs((prev) => [...prev, emptyInput()])}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Add input
              </Button>
            )}
          </Box>

          {inputs.map((input, index) => (
            <Grid
              container
              spacing={1}
              key={index}
              alignItems="center"
              sx={{
                mb: 1,
                p: 1,
                borderRadius: 2,
                bgcolor: "#fff",
                border:
                  duplicateKeyIndex === index ? "1px solid #FCA5A5" : (
                    "1px solid #E5E7EB"
                  ),
              }}
            >
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Input Key *"
                  placeholder="HCT"
                  disabled={readOnly}
                  value={input.key}
                  error={duplicateKeyIndex === index}
                  helperText={
                    duplicateKeyIndex === index ? "Key already used" : ""
                  }
                  onChange={(e) => updateInput(index, "key", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small" disabled={readOnly}>
                  <InputLabel id={`input-test-${index}`}>Test</InputLabel>
                  <Select
                    labelId={`input-test-${index}`}
                    label="Test"
                    value={input.test}
                    onChange={(e) => updateInput(index, "test", e.target.value)}
                  >
                    <MenuItem value="">Same test as output</MenuItem>
                    {tests.map((test) => (
                      <MenuItem key={test._id} value={test._id}>
                        {test.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={10} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Parameter Name *"
                  placeholder="Hematocrit"
                  disabled={readOnly}
                  value={input.parameterName}
                  onChange={(e) =>
                    updateInput(index, "parameterName", e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={2} sm={1} sx={{ textAlign: "right" }}>
                {!readOnly && (
                  <Tooltip title="Remove input">
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        aria-label="Remove input"
                        disabled={inputs.length === 1}
                        onClick={() =>
                          setInputs((prev) =>
                            prev.filter((_, i) => i !== index),
                          )
                        }
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Grid>
            </Grid>
          ))}
        </Box>

        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            size="small"
            multiline
            rows={2}
            label="Formula *"
            placeholder="HCT * 10 / RBC"
            disabled={readOnly}
            value={formula}
            error={submitted && !formula.trim()}
            helperText={
              submitted && !formula.trim() ?
                "Formula is required"
              : "Use the input keys with + - * / and parentheses. The server validates and evaluates it."
            }
            onChange={(e) => setFormula(e.target.value)}
            InputProps={{ sx: { fontFamily: "monospace" } }}
          />

          {inputs.some((input) => input.key.trim()) && (
            <Box
              sx={{
                mt: 1,
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                alignItems: "center",
              }}
            >
              <Typography
                sx={{ fontSize: 11.5, fontWeight: 700, color: "#6B7280" }}
              >
                Available keys:
              </Typography>
              {inputs
                .filter((input) => input.key.trim())
                .map((input) => (
                  <Chip
                    key={input.key}
                    size="small"
                    label={`${input.key.trim()}${
                      input.parameterName ? ` → ${input.parameterName}` : ""
                    }`}
                    sx={{ height: 20, fontSize: 11 }}
                  />
                ))}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={() => onClose(false)}
          disabled={saving}
          sx={{ textTransform: "none", color: "#6B7280", fontWeight: 600 }}
        >
          {readOnly ? "Close" : "Cancel"}
        </Button>
        {!readOnly && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving}
            sx={{ textTransform: "none", fontWeight: 700, px: 3 }}
          >
            {saving ?
              "Saving…"
            : isEdit ?
              "Save changes"
            : "Create calculation"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const PreviewDialog = ({ open, calculation, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [values, setValues] = useState({});
  const [outcome, setOutcome] = useState(null);
  const [error, setError] = useState("");
  const [running, setRunning] = useState(false);

  const fields = useMemo(
    () => buildPreviewFields(calculation?.inputs),
    [calculation],
  );

  useEffect(() => {
    if (!open) return;
    setValues({});
    setOutcome(null);
    setError("");
  }, [open, calculation]);

  const run = async () => {
    setRunning(true);
    setError("");
    const res = await evaluateLabCalculation(calculation?._id, values);
    setRunning(false);
    if (!res.ok) {
      setOutcome(null);
      setError(res.error || "Unable to evaluate the calculation.");
      return;
    }
    setOutcome(res.data);
  };

  const described = describePreview(outcome);
  const toneColor =
    described.tone === "success" ?
      { bg: "#ECFDF5", border: "#A7F3D0", color: "#065F46" }
    : described.tone === "warning" ?
      { bg: "#FFFBEB", border: "#FDE68A", color: "#92400E" }
    : { bg: "#FEF2F2", border: "#FECACA", color: "#991B1B" };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 17 }}>
          Preview calculation
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: "#6B7280" }}>
          {calculation?.name}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Typography
          sx={{
            mb: 2,
            p: 1,
            borderRadius: 2,
            bgcolor: "#F3F4F6",
            fontSize: 11.5,
            color: "#4B5563",
          }}
        >
          Enter sample values to check the configuration. This is a preview only
          — no patient result is created or changed.
        </Typography>

        {fields.length === 0 ?
          <Typography sx={{ fontSize: 14, color: "#6B7280" }}>
            This calculation has no input parameters to preview.
          </Typography>
        : <Grid container spacing={2}>
            {fields.map((field) => (
              <Grid item xs={12} sm={6} key={field.key}>
                <TextField
                  fullWidth
                  size="small"
                  label={field.key}
                  helperText={field.parameterName}
                  value={values[field.key] ?? ""}
                  onChange={(e) =>
                    setValues((prev) => ({
                      ...prev,
                      [field.key]: e.target.value,
                    }))
                  }
                />
              </Grid>
            ))}
          </Grid>
        }

        {error && (
          <Box
            role="alert"
            sx={{
              mt: 2,
              p: 1.25,
              borderRadius: 2,
              border: "1px solid #FECACA",
              bgcolor: "#FEF2F2",
              color: "#B91C1C",
              fontSize: 12.5,
              fontWeight: 600,
            }}
          >
            {error}
          </Box>
        )}

        {outcome && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 2,
              border: `1px solid ${toneColor.border}`,
              bgcolor: toneColor.bg,
              color: toneColor.color,
            }}
          >
            <Typography
              sx={{
                fontSize: 11.5,
                fontWeight: 800,
                textTransform: "uppercase",
              }}
            >
              {described.text}
            </Typography>
            {described.tone === "success" ?
              <>
                <Typography sx={{ fontSize: 26, fontWeight: 800 }}>
                  {described.value}
                </Typography>
                <Typography sx={{ fontSize: 11.5 }}>
                  {outcome.outputParameterName} · {outcome.decimalPlaces}{" "}
                  decimal place(s)
                </Typography>
              </>
            : described.missing?.length > 0 && (
                <Typography sx={{ fontSize: 12.5, mt: 0.5 }}>
                  Missing: {described.missing.join(", ")}
                </Typography>
              )
            }
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          sx={{ textTransform: "none", color: "#6B7280", fontWeight: 600 }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          onClick={run}
          disabled={running || fields.length === 0}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          {running ? "Calculating…" : "Calculate"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CalculationMasterList = () => {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(20);
  const [search, setSearch] = useState("");
  const [activeOnly, setActiveOnly] = useState("");
  const [tests, setTests] = useState([]);
  const [modal, setModal] = useState(null);
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await listLabCalculations({
      page: page + 1,
      perPage,
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(activeOnly ? { activeOnly } : {}),
    });
    setLoading(false);
    if (!res.ok) {
      toast.error(res.error || "Error while fetching calculations!");
      setRows([]);
      return;
    }
    setRows(res.data);
    setMeta(res.meta);
  }, [page, perPage, search, activeOnly]);

  useEffect(() => {
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const res = await getTests({ perPage: 1000, withAdmin: true });
        const payload = res?.data;
        setTests(
          Array.isArray(payload?.data) ?
            payload.data
          : payload?.data?.data || [],
        );
      } catch {
        setTests([]);
      }
    })();
  }, []);

  const testNameById = useMemo(
    () =>
      tests.reduce((acc, test) => {
        acc[String(test._id)] = test.name;
        return acc;
      }, {}),
    [tests],
  );

  const outputTestName = (calculation) => {
    const ref = calculation?.outputTest;
    if (!ref) return "—";
    if (typeof ref === "object") return ref.name || "—";
    return testNameById[String(ref)] || "—";
  };

  const toggleActive = async (calculation) => {
    setBusy(true);
    const res = await updateLabCalculation(calculation._id, {
      isActive: !calculation.isActive,
    });
    setBusy(false);
    if (res.ok) {
      toast.success(
        calculation.isActive ?
          "Calculation deactivated"
        : "Calculation activated",
      );
      load();
    } else {
      toast.error(res.error || "Status update failed!");
    }
  };

  const runDelete = async () => {
    setBusy(true);
    const res = await deleteLabCalculation(confirm._id);
    setBusy(false);
    if (res.ok) {
      setConfirm(null);
      toast.success("Calculation deleted successfully");
      load();
    } else {
      toast.error(res.error || "Delete failed!");
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, width: "100%", maxWidth: "100%" }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: { xs: 18, sm: 20 } }}>
            Calculation Master
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "#6B7280" }}>
            Global derived parameters such as MCV, LDL and Anion Gap. Formulas
            are evaluated by the server.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setModal({ mode: "create", calculation: null })}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Add Calculation
        </Button>
      </Box>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={5}>
          <TextField
            fullWidth
            size="small"
            label="Search"
            placeholder="Calculation or output parameter"
            value={search}
            onChange={(e) => {
              setPage(0);
              setSearch(e.target.value);
            }}
          />
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="calc-status">Status</InputLabel>
            <Select
              labelId="calc-status"
              label="Status"
              value={activeOnly}
              onChange={(e) => {
                setPage(0);
                setActiveOnly(e.target.value);
              }}
            >
              {STATUS_FILTERS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Box
        sx={{ border: "1px solid #E5E7EB", borderRadius: 2, bgcolor: "#fff" }}
      >
        {loading ?
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        : rows.length === 0 ?
          <Typography sx={{ py: 6, textAlign: "center", color: "#6B7280" }}>
            No calculations found.
          </Typography>
        : <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 900 }}>
              <TableHead sx={{ background: "#F0F4FF" }}>
                <TableRow>
                  {[
                    "Calculation",
                    "Output test",
                    "Output parameter",
                    "Formula",
                    "Decimals",
                    "Scope",
                    "Status",
                    "Actions",
                  ].map((header) => (
                    <TableCell
                      key={header}
                      sx={{ fontWeight: 700, fontSize: 12.5 }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((calculation) => {
                  const editable = canEditRecord(calculation, {
                    isPlatformAdmin: IS_PLATFORM_ADMIN,
                  });
                  const lockReason = readOnlyReason(calculation, {
                    isPlatformAdmin: IS_PLATFORM_ADMIN,
                  });

                  return (
                    <TableRow key={calculation._id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {calculation.name}
                      </TableCell>
                      <TableCell>{outputTestName(calculation)}</TableCell>
                      <TableCell>
                        {calculation.outputParameterName || "—"}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={calculation.formula || ""}>
                          <Box
                            component="code"
                            sx={{
                              fontFamily: "monospace",
                              fontSize: 11.5,
                              bgcolor: "#F3F4F6",
                              px: 0.75,
                              py: 0.25,
                              borderRadius: 1,
                            }}
                          >
                            {truncateFormula(calculation.formula)}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        {calculation.decimalPlaces ?? "—"}
                      </TableCell>
                      <TableCell>
                        <ScopeChip record={calculation} />
                      </TableCell>
                      <TableCell>
                        <StatusChip active={calculation.isActive !== false} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.25 }}>
                          <Tooltip title="Preview calculation">
                            <IconButton
                              size="small"
                              color="success"
                              aria-label="Preview calculation"
                              onClick={() => setPreview(calculation)}
                            >
                              <PlayCircleOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View calculation">
                            <IconButton
                              size="small"
                              aria-label="View calculation"
                              onClick={() =>
                                setModal({ mode: "view", calculation })
                              }
                            >
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={editable ? "Edit calculation" : lockReason}
                          >
                            <span>
                              <IconButton
                                size="small"
                                aria-label="Edit calculation"
                                disabled={!editable}
                                onClick={() =>
                                  setModal({ mode: "edit", calculation })
                                }
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={
                              editable ?
                                calculation.isActive ?
                                  "Deactivate"
                                : "Activate"
                              : lockReason
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                aria-label="Toggle status"
                                disabled={!editable || busy}
                                onClick={() => toggleActive(calculation)}
                              >
                                {calculation.isActive ?
                                  <ToggleOnIcon
                                    fontSize="small"
                                    color="success"
                                  />
                                : <ToggleOffIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={editable ? "Delete calculation" : lockReason}
                          >
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                aria-label="Delete calculation"
                                disabled={!editable}
                                onClick={() => setConfirm(calculation)}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        }

        <TablePagination
          component="div"
          count={meta?.total || 0}
          page={page}
          onPageChange={(_, next) => setPage(next)}
          rowsPerPage={perPage}
          rowsPerPageOptions={PER_PAGE_OPTIONS}
          onRowsPerPageChange={(e) => {
            setPerPage(Number(e.target.value));
            setPage(0);
          }}
        />
      </Box>

      <CalculationFormDialog
        open={Boolean(modal)}
        mode={modal?.mode}
        calculation={modal?.calculation}
        tests={tests}
        onClose={(changed, message) => {
          setModal(null);
          if (changed) {
            toast.success(message || "Saved");
            load();
          }
        }}
      />

      <PreviewDialog
        open={Boolean(preview)}
        calculation={preview}
        onClose={() => setPreview(null)}
      />

      <Dialog open={Boolean(confirm)} onClose={() => setConfirm(null)}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 17 }}>
          Delete this calculation?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>
            &quot;{confirm?.name}&quot; will be removed and will no longer be
            selectable in report templates.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setConfirm(null)}
            disabled={busy}
            sx={{ textTransform: "none", color: "#6B7280", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={runDelete}
            disabled={busy}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {busy ? "Working…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CalculationMasterList;
